import { formatUsdValue } from '@chainflip/utils/number';
import { type BarDatum, type BarTooltipProps, type ComputedDatum, ResponsiveBar } from '@nivo/bar';
import GraphLoader from './GraphLoader';
import { TransparentBars } from './TransparentBars';
import { getDateAxisTicks } from '../utils/charts';

export { type BarTooltipProps } from '@nivo/bar';

export function StackedBarChart({
  isLoading,
  isDaily,
  chartData,
  keys,
  colorCallback,
  tooltipCallback,
}: {
  chartData: BarDatum[];
  isDaily: boolean;
  isLoading: boolean;
  keys: string[];
  colorCallback: ((d: ComputedDatum<BarDatum>) => string) | string[];
  tooltipCallback: (props: BarTooltipProps<BarDatum>, isDaily: boolean) => JSX.Element;
}) {
  return (
    <GraphLoader className="-m-4 h-full w-full [&_*]:!font-aeonikRegular" loading={isLoading}>
      <ResponsiveBar
        data={chartData}
        maxValue={isLoading ? 1 : undefined}
        keys={keys}
        colors={colorCallback}
        indexBy="date"
        margin={{ top: 20, right: 20, bottom: 50, left: 62 }}
        padding={0.4}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: -60,
          tickValues: getDateAxisTicks(chartData.map((item) => item.date)),
          format: (dateString) => {
            const date = new Date(dateString);

            if (isDaily) {
              return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: 'numeric' });
            }

            return new Date(dateString).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            });
          },
        }}
        axisLeft={{
          tickSize: 0,
          tickValues: 5,
          format: (d) => (d < 0.01 ? d : formatUsdValue(d, false)),
        }}
        gridYValues={5}
        enableLabel={false}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: '#878787',
                fontFamily: 'Aeonik-Medium',
                fontSize: 12,
              },
            },
          },
          grid: {
            line: {
              stroke: '#252525',
              strokeWidth: 1,
              strokeDasharray: 2,
            },
          },
        }}
        defs={[
          {
            id: 'StackBarChart',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'inherit', opacity: 1 },
              {
                offset: 100,
                color: 'inherit',
                opacity: 0.6,
              },
            ],
          },
        ]}
        fill={[{ match: '*', id: 'StackBarChart' }]}
        animate={false}
        tooltip={(props) => tooltipCallback(props, isDaily)}
        role="application"
        layers={['grid', 'axes', 'bars', TransparentBars]}
      />
    </GraphLoader>
  );
}
